import { computed, ComputedRef, Ref, unref } from "vue";
import { useReactiveMidnight, useReactiveDate } from "./useReactiveTime";

/**
 * Utility to normalize a date to start of day (00:00:00.000)
 */
export function startOfDay(date: Date): Date {
	const d = new Date(date);
	d.setHours(0, 0, 0, 0);
	return d;
}

/**
 * Utility to normalize a date to end of day (23:59:59.999)
 */
export function endOfDay(date: Date): Date {
	const d = new Date(date);
	d.setHours(23, 59, 59, 999);
	return d;
}

/**
 * Returns reactive date range for the last N days
 * Updates automatically when the day changes (at midnight)
 * @param daysBack Number of days to go back (not including today)
 */
export function useReactiveDateRange(daysBack: number): ComputedRef<[Date, Date]> {
	const now = useReactiveDate();

	return computed(() => {
		const end = endOfDay(new Date(now.value));
		const start = startOfDay(new Date(now.value));
		start.setDate(start.getDate() - daysBack);
		return [start, end];
	});
}

/**
 * Returns reactive start of day for a given date
 * Updates when the reference date changes
 */
export function useReactiveStartOfDay(date: Ref<Date> | Date): ComputedRef<Date> {
	return computed(() => {
		const d = unref(date);
		return startOfDay(d);
	});
}

/**
 * Returns reactive end of day for a given date
 * Updates when the reference date changes
 */
export function useReactiveEndOfDay(date: Ref<Date> | Date): ComputedRef<Date> {
	return computed(() => {
		const d = unref(date);
		return endOfDay(d);
	});
}

/**
 * Returns "today at midnight" for use as max date bound
 * Auto-updates at midnight to prevent future date selection after day rollover
 */
export function useReactiveTodayMidnight(): ComputedRef<Date> {
	return useReactiveMidnight();
}
